// src/ui/components/dialogs/FullscreenDialog.utils.ts

/**
 * Procesa HTML eliminando todos los estilos inline para uniformidad
 * Convierte saltos de línea de texto plano en <br> tags para preservarlos
 */
export const processHtml = (html: string): string => {
  if (!html || !html.trim()) return html;
  
  let processed = html
    .replace(/\s+style="[^"]*"/gi, '')
    .replace(/\s+style='[^']*'/gi, '');
  
  // Método simple y robusto: proteger tags HTML y convertir saltos de línea
  const tagPlaceholders: string[] = [];
  let placeholderIndex = 0;
  
  // 1. Reemplazar todos los tags HTML (abiertos y cerrados) con placeholders temporales
  processed = processed.replace(/<[^>]+>/g, (match) => {
    const placeholder = `___TAG_${placeholderIndex}___`;
    tagPlaceholders[placeholderIndex] = match;
    placeholderIndex++;
    return placeholder;
  });
  
  // 2. Unir palabras relacionadas que están separadas por saltos de línea (\n o \r\n)
  // Detectar patrones como: palabra en mayúsculas + salto de línea + palabra en mayúsculas
  // Ejemplo: "MUSCLE\nCLEAN" → "MUSCLE CLEAN"
  // Hacer múltiples pasadas para capturar todos los casos
  let previousProcessed = '';
  let iterations = 0;
  while (previousProcessed !== processed && iterations < 5) {
    previousProcessed = processed;
    processed = processed.replace(/([A-Z][A-Z\s]{1,20}?)\s*\r?\n\s*([A-Z][A-Z\s]{1,20}?)(?=\s*\r?\n|$|[a-z0-9])/g, (match, word1, word2) => {
      const trimmedWord1 = word1.trim();
      const trimmedWord2 = word2.trim();
      
      // Solo unir si ambas son palabras en mayúsculas sin números o puntuación especial
      // Y si tienen entre 1 y 20 caracteres (para evitar unir líneas completas)
      if (
        /^[A-Z\s]{1,20}$/.test(trimmedWord1) && 
        /^[A-Z\s]{1,20}$/.test(trimmedWord2) &&
        !trimmedWord1.match(/[0-9]/) &&
        !trimmedWord2.match(/[0-9]/) &&
        trimmedWord1.length >= 2 &&
        trimmedWord2.length >= 2
      ) {
        return `${trimmedWord1} ${trimmedWord2}`;
      }
      return match; // Mantener el salto de línea original si no cumple los criterios
    });
    iterations++;
  }
  
  // 3. Convertir saltos de línea (\n o \r\n) en <br /> tags
  processed = processed.replace(/\r?\n/g, '<br />');
  
  // 4. Restaurar los tags HTML protegidos
  tagPlaceholders.forEach((tag, index) => {
    processed = processed.replace(`___TAG_${index}___`, tag);
  });
  
  // 5. DESPUÉS de restaurar tags, unir palabras separadas por <br /> tags
  // Esto captura casos donde ya había <br /> en el HTML original
  // Hacer múltiples pasadas para capturar todos los casos
  let previousProcessed2 = '';
  let iterations2 = 0;
  while (previousProcessed2 !== processed && iterations2 < 5) {
    previousProcessed2 = processed;
    processed = processed.replace(/([A-Z][A-Z\s]{1,20}?)\s*<br\s*\/?>\s*([A-Z][A-Z\s]{1,20}?)(?=\s*<br|$|[a-z0-9<]|\s*[A-Z]{3,})/gi, (match, word1, word2) => {
      const trimmedWord1 = word1.trim();
      const trimmedWord2 = word2.trim();
      
      // Solo unir si ambas son palabras en mayúsculas sin números
      // Y si son palabras cortas (máximo 10 caracteres cada una) que claramente deberían estar juntas
      if (
        /^[A-Z\s]{2,10}$/.test(trimmedWord1) && 
        /^[A-Z\s]{2,10}$/.test(trimmedWord2) &&
        !trimmedWord1.match(/[0-9]/) &&
        !trimmedWord2.match(/[0-9]/) &&
        trimmedWord1.length >= 2 &&
        trimmedWord2.length >= 2 &&
        trimmedWord1.split(/\s+/).length <= 2 && // Máximo 2 palabras en word1
        trimmedWord2.split(/\s+/).length <= 2    // Máximo 2 palabras en word2
      ) {
        return `${trimmedWord1} ${trimmedWord2}`;
      }
      return match;
    });
    iterations2++;
  }
  
  return processed;
};

/**
 * Calcula el factor de escala de texto según la resolución de pantalla
 * Solo aumenta para pantallas 2K+, resto mantiene 1.2
 */
export const getTextScale = (): number => {
  if (typeof window === 'undefined') return 1.2; // SSR fallback

  const width = window.innerWidth;
  const height = window.innerHeight;

  // Solo para pantallas 2K+ aumentar el tamaño
  if (width >= 2560 || height >= 1440) return 2.0; // 2K+: texto 2x más grande
  return 1.2; // Resto mantiene el tamaño actual que funciona bien
};

/**
 * Detecta si el media HTML es una imagen
 */
export const isImageMedia = (html: string): boolean => {
  if (!html) return false;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const firstElement = doc.body.firstElementChild;
    return firstElement?.tagName === 'IMG';
  } catch {
    return false;
  }
};

/**
 * Extrae la URL de imagen del HTML
 */
export const extractImageUrl = (html: string): string => {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const img = doc.body.querySelector('img');
    return img?.getAttribute('src') || img?.getAttribute('data-src') || '';
  } catch {
    return '';
  }
};

/**
 * Optimiza URL de YouTube para mejor comportamiento en Raspberry Pi
 * Fuerza calidad más baja para mejor rendimiento
 * @param src URL de YouTube original
 * @param isRaspberryPi Si es true, aplica optimizaciones más agresivas
 */
export const optimizeYouTubeUrl = (src: string, isRaspberryPi: boolean = false): string => {
  if (src.includes('youtube.com') || src.includes('youtu.be')) {
    const separator = src.includes('?') ? '&' : '?';
    
    // Parámetros base optimizados para mejor rendimiento
    const baseParams = [
      'enablejsapi=1',
      'rel=0', // No mostrar videos relacionados
      'modestbranding=1', // Menos branding
      'iv_load_policy=3', // No mostrar anotaciones
      'controls=1', // Mostrar controles básicos
      'disablekb=1', // Deshabilitar teclado (mejor rendimiento)
      'playsinline=1', // Reproducir inline (mejor para móviles/dispositivos)
      'cc_load_policy=0', // Deshabilitar subtítulos por defecto (menos carga)
      'autoplay=1', // Autoplay
      'mute=1', // Mute por defecto (requerido para autoplay)
    ];
    
    // Calidad de video: tiny es la más baja disponible (mejor para Raspberry Pi)
    const qualityParam = isRaspberryPi ? 'vq=tiny' : 'vq=small';
    
    // Agregar origin para evitar restricciones CORS
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const originParam = origin ? `origin=${encodeURIComponent(origin)}` : '';
    
    const allParams = [...baseParams, qualityParam];
    if (originParam) {
      allParams.push(originParam);
    }
    
    return `${src}${separator}${allParams.join('&')}`;
  }
  return src;
};

/**
 * Resetea todos los iframes y videos en un contenedor
 */
export const resetMediaElements = (container: HTMLElement): void => {
  // Resetear iframes
  const iframes = container.querySelectorAll('iframe');
  iframes.forEach((iframe) => {
    const originalSrc = iframe.src;
    iframe.src = '';
    setTimeout(() => {
      iframe.src = originalSrc;
    }, 100);
  });

  // Resetear videos nativos
  const videos = container.querySelectorAll('video');
  videos.forEach((video) => {
    video.pause();
    video.currentTime = 0;
    video.load();
  });
};




